/**
 * THE REPRICE NOTICE — one sentence, one module, two pages.
 *
 * The owner ruled "re-price everything": changing a company's tax rate rewrites
 * quotes and invoices **already sent to customers**, including ones paid and
 * sitting in Xero, with no undo and no exceptions. He was offered a narrower
 * option twice and refused it both times.
 *
 * `line_tax` is a GENERATED column and keeps no history, so without a record
 * the previous figure is gone for ever. Migration 097 (deployed
 * `20260829123506-c323947`) adds the record: three columns on a quote and on an
 * invoice, carrying the figure the customer's copy shows and the currency it
 * was in. It turns "no undo and no record" into "no undo, but a record" — and
 * this module is the only place that renders it, because the text describes
 * money on a customer-facing document and two copies of it would agree the day
 * they were written and drift on the first edit.
 *
 * ── WHAT LOOKS LIKE A BUG AND IS NOT ───────────────────────────────────────
 *
 * ⚠️ **All three fields are null TOGETHER.** `repricedAt === null` guarantees
 * the other two are null. That is a deliberate serializer decision, not a
 * coincidence to code around: the underlying columns are populated on *every*
 * pushed document including untouched ones, and passed straight through they
 * would print *"was 35,000.00"* beside an unchanged 35,000.00. Test
 * `repricedAt` and trust the rest.
 *
 * ⚠️ **A document that has NEVER been pushed to Xero reports all three null,
 * permanently** — the anchor is the last successful push. So this renders
 * nothing on a draft, and **that is correct, not a broken feature.** There is
 * deliberately no fallback that guesses a previous figure from anywhere else,
 * because nothing else records one.
 *
 * ⚠️ **A re-push CLEARS the flag**, correctly: the customer now holds the
 * current figure, so there is nothing left to warn about.
 *
 * ⚠️ **Nothing is backfilled and a backfill is impossible** — nothing anywhere
 * records what a total used to be, which is the whole reason these columns
 * exist.
 *
 * ── THE ONE HONEST LIMIT, RECORDED HERE AND NOT ON SCREEN ──────────────────
 *
 * ⚠️ **This is not a general audit of the number.** `grandTotal` is writable
 * directly, and a PATCH that sets it does **not** stamp — the stamp fires off a
 * LINE mutation. So the notice reports every reprice that happens the ordinary
 * way and is **silent about a total somebody typed into the header.**
 *
 * That limit is deliberately not in the rendered copy. Saying "we catch line
 * changes but not typed totals" tells a reader nothing they can act on and
 * invites them to treat the notice's ABSENCE as a guarantee the total has not
 * moved, which is exactly what it is not. The copy therefore states what it
 * knows and claims nothing about what it does not — but nobody extending this
 * may write a sentence implying every change is caught.
 *
 * ── WHY IT IS NOT HELPER TEXT ──────────────────────────────────────────────
 *
 * ⛔ **Do not delete this in a copy sweep.** `CLAUDE.md`'s NO HELPER TEXT rule
 * carries an exception list, and the test is *"what wrong conclusion does this
 * prevent?"* This one names an irreversible consequence the reader cannot
 * otherwise see: **the customer is holding a different figure from the one on
 * screen.** Nothing else on either page says so, and the record itself looks
 * entirely healthy.
 */
import { formatDateTime, formatDecimalString } from '@/lib/format';
import type { Quote } from '@/lib/types';

/**
 * Just the three fields the notice reads.
 *
 * A whole `Quote` satisfies this and so does a whole `Invoice` — the trio is
 * field for field the same on both — which is what lets one module serve two
 * pages without either of them casting. A narrow parameter is also what stops
 * this reaching for the document's own `currency`; it cannot see one.
 */
export type RepricedDocument = Pick<
  Quote,
  'repricedAt' | 'repricedFromGrandTotal' | 'repricedFromCurrency'
>;

/** The rendered notice: a headline and the sentence under it. */
export interface RepriceNotice {
  headline: string;
  body: string;
}

/**
 * The notice for a document whose total has moved since the customer's copy was
 * produced, or `null` when there is nothing to say.
 *
 * ⚠️ **The currency is `repricedFromCurrency` and must NEVER be the document's
 * current `currency`.** `currency` is an ordinary editable column, so a reprice
 * can stamp at time T and somebody can change the currency at T+1 — printing
 * the old amount under the new code. That looks entirely normal on screen and
 * is the `currencyRate` incident (a figure ~69× overstated on a customer's
 * document) in a new field. **A number without its unit is not the fact**,
 * which is also why an amount arriving without its currency renders nothing at
 * all rather than a bare figure: the contract says that cannot happen, and if
 * it ever does, silence is the only honest rendering.
 *
 * ⚠️ **The money never sees a float.** `formatDecimalString` groups the exact
 * decimal string as text; `Number()` on it would lose pennies, and `?? 0` would
 * invent a total the customer never saw.
 *
 * ⚠️ **`repricedAt` is a real timestamp, not a plain 'YYYY-MM-DD'**, so it goes
 * through `formatDateTime` and never `formatDateOnly`. The date-only helper
 * slices at the 'T' and formats the remainder in UTC — correct for a column
 * that carries no time, and wrong here: a stamp at 23:45 UTC would print as the
 * previous day for every viewer east of Greenwich, on a notice whose whole
 * subject is when something changed.
 *
 * The wording says the customer's copy **may** show the old figure, never
 * *does*: we know what was sent, not what anybody has looked at. And it offers
 * no way to re-push from here — re-pushing rewrites a live accounting document
 * and re-numbers a quote in Xero, which is a separate decision with its own
 * consequences, not the remedy for a sentence.
 */
export function repriceNotice(document: RepricedDocument): RepriceNotice | null {
  const { repricedAt, repricedFromGrandTotal, repricedFromCurrency } = document;
  if (repricedAt === null || repricedFromGrandTotal === null || repricedFromCurrency === null) {
    return null;
  }

  return {
    headline: `This document's total changed from ${formatDecimalString(repricedFromGrandTotal)} ${repricedFromCurrency} on ${formatDateTime(repricedAt)}.`,
    body: 'The copy the customer holds may show the old figure.',
  };
}
